/*
Frontend setup for our product analytics with Segment and PostHog.

With Segment, we track:
- page views
- events
- identify calls
These are also pushed into PostHog via Segment.

With PostHog, we track:
- screen recordings
- autocapture events
these are sent directly to PostHog.

Most similar to the second option here:
https://posthog.com/docs/advanced/cdp#option-2-use-a-3rd-party-cdp-with-posthog-as-a-destination
*/
import { AnalyticsBrowser } from "@segment/analytics-next";
import posthog from "posthog-js";
import { isProd, devlog } from "./utils";

const cdnURL = isProd
  ? "https://analytics.mainshares.com"
  : "https://cdn.segment.com";

const cdnOptions = { writeKey: process.env.ESBUILD_SEGMENT_API_KEY, cdnURL };

let trackingOptions = {};
if (isProd) {
  trackingOptions = {
    integrations: {
      "Segment.io": {
        apiHost: "track.mainshares.com/v1",
      },
    },
  };
}

export const analytics = new AnalyticsBrowser();

analytics.load(cdnOptions, trackingOptions);

analytics.addSourceMiddleware(({ payload, next }) => {
  // Parse URL query parameters (if running in browser context)
  const params = new URLSearchParams(window.location.search);
  if (params.get("fbclid") || params.get("gclid") || params.get("utm_source")) {
    const campaignData = {
      name: params.get("utm_campaign"),
      source: params.get("utm_source"),
      medium: params.get("utm_medium"),
    };
    if (params.get("utm_term")) {
      campaignData.term = params.get("utm_term");
    }
    if (params.get("utm_content")) {
      campaignData.content = params.get("utm_content");
    }
    if (params.get("gclid")) {
      campaignData.gclid = params.get("gclid");
    }
    if (params.get("fbclid")) {
      campaignData.fbclid = params.get("fbclid");
    }

    // Merge campaign data into the event's context object
    payload.obj.context = {
      ...payload.obj.context, // preserve existing context (if any)
      campaign: campaignData, // add the campaign info under context.campaign
    };
  }
  next(payload);
});

analytics.ready(() => {
  devlog("analytics ready");
  posthog.init(process.env.ESBUILD_POSTHOG_API_KEY, {
    api_host: "https://us.posthog.com",
    capture_pageview: false, // covered by Segment
    debug: !isProd,
    enable_recording_console_log: true,
    loaded: () => {
      trackPageView();
      devlog("analytics loaded");
    },
    rageclick: true,
    segment: analytics,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: { password: true },
    },
  });
});

export const identify = (userId, attrs) => {
  analytics.identify(userId, attrs);
  devlog(`identified user ${userId} with attrs ${JSON.stringify(attrs)}`);
};

export const trackEvent = (event, attrs) => {
  analytics.track(event, attrs);
};

window.lastPage = "";

function trackPageView() {
  let pageNotYetTracked = window.lastPage !== window.location.pathname;

  if (pageNotYetTracked) {
    devlog(`tracking ${window.location.pathname}`);
    analytics.page(window.location.pathname, {});
  }

  window.lastPage = window.location.pathname;
}

export const identifyWithLocalStorageUTM = (userId, traits) => {
  // grab campaign data saved by marketing site if it exists
  const stringifiedCampaignData = localStorage.getItem("campaign");
  let attrs = traits;
  if (stringifiedCampaignData) {
    const campaignData = {
      context: { campaign: JSON.parse(stringifiedCampaignData) },
    };
    attrs = { ...traits, ...campaignData };
  }

  // parse ajs_anonymous_id from localStorage, or fall back to the raw value of anonymousId
  const anonymousId =
    JSON.parse(localStorage.getItem("ajs_anonymous_id")) ||
    localStorage.getItem("anonymousId");
  if (anonymousId) {
    attrs.anonymousId = anonymousId;
    devlog(`identified user with anonymousId ${anonymousId}`);
  }

  identify(userId, attrs);
};

window.addEventListener("phx:identify-user", (info) => {
  identifyWithLocalStorageUTM(info.detail.userId, info.detail.traits);
});

window.addEventListener("phx:track", (info) => {
  analytics.track(info.detail.event, info.detail.attrs);
});

window.addEventListener("phx:page-loading-stop", (info) => {
  // when someone goes to a different live view page
  if (["redirect", "patch"].includes(info.detail.kind)) {
    trackPageView();
  }
});
